import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { MobileFrame } from './components/ui/MobileFrame';
import { BottomTabBar } from './components/ui/BottomTabBar';
import { ThemeProvider } from './contexts/ThemeContext';
import { IncomingCallBanner } from './components/ui/IncomingCallBanner';
import { EmergencyCallNotification } from './components/ui/EmergencyCallNotification';
import { VexuNotification } from './components/ui/VexuNotification';
import { CancellationSuccessToast } from './components/billing/CancellationSuccessToast';
// New Onboarding Screens
import { WelcomeScreen } from './pages/WelcomeScreen';
import { FeatureCarouselScreen } from './pages/FeatureCarouselScreen';
import { PhoneEntryScreen } from './pages/PhoneEntryScreen';
import { OTPScreen } from './pages/OTPScreen';
import { NameEntryScreen } from './pages/NameEntryScreen';
import { BackupCheckScreen } from './pages/BackupCheckScreen';
// Main App Screens
import { HomeScreen } from './pages/HomeScreen';
import { CalendarScreen } from './pages/CalendarScreen';
import { BookingsScreen } from './pages/BookingsScreen';
import { CallsScreen } from './pages/CallsScreen';
import { CallDetailScreen } from './pages/CallDetailScreen';
import { ActiveCallScreen } from './pages/ActiveCallScreen';
import { UsageScreen } from './pages/UsageScreen';
import { ProfileScreen } from './pages/ProfileScreen';
import { SettingsScreen } from './pages/SettingsScreen';
import { CallerExperienceScreen } from './pages/CallerExperienceScreen';
import { PlanSelectionScreen } from './pages/PlanSelectionScreen';
import { SubscriptionConsentScreen } from './pages/SubscriptionConsentScreen';
import { ActivationGuideScreen } from './pages/ActivationGuideScreen';
import { SupportChatScreen } from './pages/SupportChatScreen';
import { NotificationsScreen } from './pages/NotificationsScreen';
import { ProCheckoutScreen } from './pages/ProCheckoutScreen';
import { ProActivationSuccessScreen } from './pages/ProActivationSuccessScreen';
import { IosActivationFlow } from './pages/IosActivationFlow';
import { IosDeactivationFlow } from './pages/IosDeactivationFlow';
import type { RuleId, Condition } from './data/mobileForwarding';
import type { DashboardState, ForwardingAction, ForwardingConnection, JourneyProgress, JourneyStart } from './types/forwarding';
import { activeRuleIds, mergeJourney, reactivationStart, resumeStart, rulesNeedingSetup, savedConditionalRules, turnAllOff, turnRuleOff } from './utils/forwardingStatus';
import { DEMO_CONNECTIONS } from './data/forwardingDemoStates';
import { ForwardingSetupScreen } from './pages/ForwardingSetupScreen';
import { DEFAULT_SUBSCRIPTION_PLAN_ID, getSubscriptionPlan, SubscriptionPlanId } from './data/subscriptionPlans';
import { LandlineDeactivationScreen } from './pages/LandlineDeactivationScreen';
import { OnboardingTour, TourStep } from './components/ui/OnboardingTour';
import { useScreenInit } from './useScreenInit.js';
import { AccessLogin } from './components/auth/AccessLogin';
import { hasAccessSession, hasRememberedAccess } from './utils/accessAuth';
// The only in-app tip: shown once, the first time the user opens Calls.
const CALLS_TOUR_STEPS: TourStep[] = [{
  id: 'calls-caller-experience',
  title: 'Tune the experience',
  description: 'Customize how Vexu greets and responds to different callers from Caller Experience.',
  spotlightSelector: '[data-tour="calls-caller-experience"]',
  prefer: 'below',
  highlightPadding: 8
}];
interface AppProps {
  /** Internal testing tools inside the app (e.g. Simulate call history). Off for production. */
  showDemoTools?: boolean;
}
export function App({
  showDemoTools = true
}: AppProps) {
  const [hasAccess, setHasAccess] = useState(() => hasAccessSession() || hasRememberedAccess());
  if (!hasAccess) return <AccessLogin onSuccess={() => setHasAccess(true)} data-id="element-0" data-mp="apr6z3excwrv" />;
  return <VexuPrototype showDemoTools={showDemoTools} data-id="element-1" data-mp="arc14rxbudp7" />;
}
function VexuPrototype({
  showDemoTools = true
}: AppProps) {
  const screenInit = useScreenInit();
  const routeScreenMap: Record<string, string> = {
    '/forwardingSetup': 'forwardingSetup',
    '/iosDeactivation': 'iosDeactivation',
    '/planSelection': 'planSelection',
    '/subscriptionConsent': 'subscriptionConsent',
    '/proCheckout': 'proCheckout',
    '/home': 'home'
  };
  // Screens previews of each Home dashboard state seed an example account.
  const homeDemoState = screenInit.homeState as DashboardState | undefined;
  const initialScreen = typeof window !== 'undefined' ? routeScreenMap[window.location.pathname] ?? 'welcome' : 'welcome';
  const initialPlanId: SubscriptionPlanId = screenInit.selectedPlanId === 'growth' || screenInit.selectedPlanId === 'scale' ? screenInit.selectedPlanId : DEFAULT_SUBSCRIPTION_PLAN_ID;
  // State for navigation and onboarding
  const [currentScreen, setCurrentScreen] = useState(initialScreen);
  const [onboardingComplete, setOnboardingComplete] = useState(initialScreen === 'home');
  const [planOrigin, setPlanOrigin] = useState('home');
  const [showCallsTour, setShowCallsTour] = useState(false);
  const [callsTourSeen, setCallsTourSeen] = useState(false);
  const [activeTab, setActiveTab] = useState('home');
  // Store phone number and selected OTP delivery method during onboarding
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationMethod, setVerificationMethod] = useState<'whatsapp' | 'call'>('whatsapp');
  const [callDetailOpen, setCallDetailOpen] = useState(false);
  const [callToOpenId, setCallToOpenId] = useState<number | null>(null);
  const [calendarEventToOpenId, setCalendarEventToOpenId] = useState<number | null>(null);
  // Activation state (set after plan selection)
  const [isActivated, setIsActivated] = useState(Boolean(homeDemoState && homeDemoState !== 'firstTime'));
  // The connected number and what we actually know about each forwarding
  // rule. Turning forwarding off keeps this (rules marked off) so a returning
  // user is never treated as first-time.
  const [connection, setConnection] = useState<ForwardingConnection | null>(homeDemoState ? DEMO_CONNECTIONS[homeDemoState] ?? null : null);
  const accountNumber = phoneNumber || '+1 (415) 555-0142';
  // The number type selected immediately after subscription determines the
  // appropriate forwarding and deactivation instructions.
  const [forwardingNumberType, setForwardingNumberType] = useState<'mobile' | 'landline' | null>(screenInit.forwardingNumberType === 'landline' ? 'landline' : null);
  // The selector is shared by post-checkout setup and Home. Keep its return
  // destination explicit so Home re-entry never leads back to checkout.
  const [forwardingSetupOrigin, setForwardingSetupOrigin] = useState<'checkout' | 'home'>('checkout');
  // Returning users re-enter the activation journey with their saved setup.
  const [journeyStart, setJourneyStart] = useState<JourneyStart | null>(null);
  // A fresh journey (new or changed number) replaces the saved configuration.
  const freshJourneyRef = useRef(false);
  // Rule currently being turned off, the ones still queued after it, and
  // whether the user is turning everything off or just one rule.
  const [deactivateTarget, setDeactivateTarget] = useState<string | null>(null);
  const [deactivateQueue, setDeactivateQueue] = useState<RuleId[]>([]);
  const [deactivateIntent, setDeactivateIntent] = useState<'all' | 'single'>('all');
  // Indicates that a carrier deactivation was started as part of cancelling the subscription.
  const [cancelAfterForwardingDeactivation, setCancelAfterForwardingDeactivation] = useState(false);
  const [cancellationNotice, setCancellationNotice] = useState<string | null>(null);
  const dismissCancellationNotice = useCallback(() => setCancellationNotice(null), []);
  // Show the first-run welcome banner on Caller Experience (set when the user
  // taps "Customize My AI" right after activation).
  const [callerExpWelcome, setCallerExpWelcome] = useState(false);
  // Billing state persists across the staged plan → consent → payment journey.
  const [selectedPlanId, setSelectedPlanId] = useState<SubscriptionPlanId>(initialPlanId);
  const selectedPlan = getSubscriptionPlan(selectedPlanId);
  // The promotional trial can be enabled or withdrawn without changing the plan itself.
  const [hasTrialOffer, setHasTrialOffer] = useState(screenInit.hasTrialOffer !== false);
  // Vexu Promote: opt-in for 20% bonus minutes, managed from checkout and Usage.
  const [promoEnabled, setPromoEnabled] = useState(false);
  // A promo code is entered while choosing a plan, before the consent and payment steps.
  const [promoCode, setPromoCode] = useState('');
  // Trial state — active but within the 3-day free trial (no charge yet)
  const [isTrial, setIsTrial] = useState(false);
  // Usage simulation state (driven by external controls)
  const [simOverage, setSimOverage] = useState(false);
  const handleSimulateFree = () => {
    setIsActivated(false);
    setIsTrial(false);
    setSimOverage(false);
    handleNavigate('usage');
  };
  const handleSimulateTrial = () => {
    setIsActivated(true);
    setIsTrial(true);
    setSimOverage(false);
    handleNavigate('usage');
  };
  const handleSimulateActive = () => {
    setIsActivated(true);
    setIsTrial(false);
    setSimOverage(false);
    handleNavigate('usage');
  };
  const handleSimulateOverage = () => {
    setIsActivated(true);
    setIsTrial(false);
    setSimOverage(true);
    handleNavigate('usage');
  };
  const completeSubscriptionCancellation = () => {
    setCancellationNotice(isTrial ? "Your trial ends without a charge. Your settings are saved if you come back." : 'Your plan stays active until the end of this billing period. Your settings are saved.');
    setIsActivated(false);
    setIsTrial(false);
    // Keep the connected number and saved rules — only forwarding is off.
    setConnection(prev => prev ? turnAllOff(prev) : prev);
    setVexuStatus(null);
    setDeactivateTarget(null);
    setDeactivateQueue([]);
    setCancelAfterForwardingDeactivation(false);
    handleNavigate('home');
  };
  const previewConsentOffer = (hasTrial: boolean) => {
    setSelectedPlanId(DEFAULT_SUBSCRIPTION_PLAN_ID);
    setHasTrialOffer(hasTrial);
    setIsActivated(false);
    setIsTrial(false);
    setSimOverage(false);
    handleNavigate('subscriptionConsent');
  };
  // Incoming call simulation state
  const [incomingCall, setIncomingCall] = useState<any>(null);
  // Emergency demo: OS notification card + the timers that advance the demo.
  const [showEmergencyNotification, setShowEmergencyNotification] = useState(false);
  const emergencyTimersRef = useRef<number[]>([]);
  // Demo alerts auto-hide after this long, or when their console button is tapped again.
  const ALERT_DURATION_MS = 6000;
  const callTimerRef = useRef<number | null>(null);
  const vexuTimerRef = useRef<number | null>(null);
  const clearTimer = (ref: React.MutableRefObject<number | null>) => {
    if (ref.current !== null) window.clearTimeout(ref.current);
    ref.current = null;
  };
  const clearEmergencyTimers = () => {
    emergencyTimersRef.current.forEach(id => window.clearTimeout(id));
    emergencyTimersRef.current = [];
  };
  useEffect(() => () => {
    emergencyTimersRef.current.forEach(id => window.clearTimeout(id));
    if (callTimerRef.current !== null) window.clearTimeout(callTimerRef.current);
    if (vexuTimerRef.current !== null) window.clearTimeout(vexuTimerRef.current);
  }, []);
  // Persistent Vexu system notification (sticky while call forwarding is active)
  const [vexuStatus, setVexuStatus] = useState<'active' | 'busy' | 'dnd' | null>(null);
  const handleToggleVexuNotification = () => {
    clearTimer(vexuTimerRef);
    if (vexuStatus !== null) {
      setVexuStatus(null);
      return;
    }
    setVexuStatus('active');
    vexuTimerRef.current = window.setTimeout(() => {
      vexuTimerRef.current = null;
      setVexuStatus(null);
    }, ALERT_DURATION_MS);
  };
  // Navigation handlers
  const handleGetStarted = () => setCurrentScreen('features');
  const handleExplore = () => {
    setOnboardingComplete(true);
    setCurrentScreen('home');
  };
  const handleFeaturesComplete = () => setCurrentScreen('phoneEntry');
  const handlePhoneSubmit = (phone: string, deliveryMethod: 'whatsapp' | 'call') => {
    setPhoneNumber(phone);
    setVerificationMethod(deliveryMethod);
    setCurrentScreen('otp');
  };
  const handleOtpVerify = () => {
    setCurrentScreen('backupCheck');
  };
  const handleNameComplete = (name: string) => {
    setOnboardingComplete(true);
    setCurrentScreen('home');
  };
  // Start the Calls tour the first time the user lands on the Calls list
  const maybeStartCallsTour = (screen: string) => {
    if (screen === 'calls' && !callsTourSeen) {
      setCallsTourSeen(true);
      setShowCallsTour(true);
    }
  };
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setCurrentScreen(tab);
    maybeStartCallsTour(tab);
  };
  const handleNavigate = (screen: string) => {
    // Remember where the user opened plans from, so Back returns there
    // (stepping back from consent/payment keeps the original origin).
    if (screen === 'planSelection' && !['planSelection', 'subscriptionConsent', 'proCheckout'].includes(currentScreen)) {
      setPlanOrigin(currentScreen);
    }
    setCurrentScreen(screen);
    if (['home', 'calendar', 'calls', 'usage', 'profile'].includes(screen)) {
      setActiveTab(screen);
    }
    maybeStartCallsTour(screen);
  };
  const [showOnboardingWarning, setShowOnboardingWarning] = useState(false);
  const handleSimulateCall = () => {
    if (!onboardingComplete) {
      setShowOnboardingWarning(true);
      setTimeout(() => setShowOnboardingWarning(false), 4000);
      return;
    }
    const callers = [{
      name: 'Alex Johnson',
      initials: 'AJ',
      colorLight: 'bg-blue-100 text-blue-700',
      colorDark: 'bg-blue-100/60 text-blue-600',
      group: 'Work Contacts'
    }, {
      name: 'Emily Chen',
      initials: 'EC',
      colorLight: 'bg-emerald-100 text-emerald-700',
      colorDark: 'bg-emerald-100/60 text-emerald-600',
      group: 'VIP Clients'
    }, {
      name: 'David Park',
      initials: 'DP',
      colorLight: 'bg-purple-100 text-purple-700',
      colorDark: 'bg-purple-100/60 text-purple-600',
      group: 'Personal'
    }];
    clearTimer(callTimerRef);
    // Tapping again while a call is showing dismisses it.
    if (incomingCall && !incomingCall.isEmergency) {
      setIncomingCall(null);
      return;
    }
    clearEmergencyTimers();
    setShowEmergencyNotification(false);
    const randomCaller = callers[Math.floor(Math.random() * callers.length)];
    setIncomingCall(randomCaller);
    callTimerRef.current = window.setTimeout(() => {
      callTimerRef.current = null;
      setIncomingCall((prev: any) => prev?.name === randomCaller.name && !prev?.isEmergency ? null : prev);
    }, ALERT_DURATION_MS);
  };
  // One guided demo that walks the emergency call through all three states:
  // notification bar → active call → completed entry in the call log.
  const handleSimulateEmergencyCall = () => {
    if (!onboardingComplete) {
      setShowOnboardingWarning(true);
      setTimeout(() => setShowOnboardingWarning(false), 4000);
      return;
    }
    clearEmergencyTimers();
    clearTimer(callTimerRef);
    // Tapping again while the alert is showing dismisses it.
    if (showEmergencyNotification) {
      setShowEmergencyNotification(false);
      setIncomingCall(null);
      return;
    }
    setCallToOpenId(null);
    // Don't let the first-run Calls tour interrupt the emergency demo.
    setCallsTourSeen(true);
    setShowCallsTour(false);
    setIncomingCall({
      name: 'Michael Torres',
      initials: 'MT',
      colorLight: 'bg-red-100 text-red-700',
      colorDark: 'bg-red-500/20 text-red-400',
      group: 'Emergency',
      isEmergency: true
    });
    // 1. Land on Home so the system notification is what the user sees first.
    handleNavigate('home');
    setShowEmergencyNotification(true);
    // 2. Tapping the alert opens the live call; left alone it clears after 6s.
    emergencyTimersRef.current.push(window.setTimeout(() => {
      emergencyTimersRef.current = [];
      setShowEmergencyNotification(false);
      setIncomingCall((prev: any) => prev?.isEmergency ? null : prev);
    }, ALERT_DURATION_MS));
  };
  const openEmergencyCall = () => {
    clearEmergencyTimers();
    setShowEmergencyNotification(false);
    handleNavigate('calls');
    setCallToOpenId(999);
  };
  // ─── Call forwarding management ───
  const chooseForwardingNumber = () => {
    setForwardingSetupOrigin('home');
    handleNavigate('forwardingSetup');
  };
  const openJourney = (start: JourneyStart | null) => {
    freshJourneyRef.current = start === null;
    setJourneyStart(start);
    if (start === null) setForwardingNumberType('mobile');
    handleNavigate('iosActivation');
  };
  const saveJourney = (p: JourneyProgress, finished: boolean) => {
    const fresh = freshJourneyRef.current;
    freshJourneyRef.current = false;
    setConnection(prev => mergeJourney(fresh ? null : prev, p, {
      numberType: 'mobile',
      phoneNumber: accountNumber
    }, finished));
  };
  const startTurnOff = (rules: RuleId[], intent: 'all' | 'single') => {
    if (!connection || rules.length === 0) return;
    setDeactivateIntent(intent);
    if (connection.numberType === 'landline') {
      setDeactivateTarget('all');
      setDeactivateQueue([]);
    } else {
      setDeactivateTarget(rules[0]);
      setDeactivateQueue(rules.slice(1));
    }
    handleNavigate('iosDeactivation');
  };
  const applyRuleOff = (rule: string) => setConnection(prev => {
    if (!prev) return prev;
    if (prev.numberType === 'landline') return turnAllOff(prev);
    return deactivateIntent === 'all' ? {
      ...prev,
      rules: {
        ...prev.rules,
        [rule]: 'off'
      }
    } : turnRuleOff(prev, rule as RuleId);
  });
  const finishDeactivation = () => {
    setDeactivateTarget(null);
    setDeactivateQueue([]);
    setCancelAfterForwardingDeactivation(false);
    handleNavigate('home');
  };
  const handleForwardingAction = (a: ForwardingAction) => {
    if (a.type === 'customize') return handleNavigate('callerExperience');
    if (a.type === 'changeNumber') return chooseForwardingNumber();
    if (a.type === 'setUp') return isActivated ? chooseForwardingNumber() : handleNavigate('planSelection');
    const c = connection;
    if (!c) return chooseForwardingNumber();
    if (a.type === 'turnOff') return startTurnOff(activeRuleIds(c), 'all');
    if (a.type === 'turnOffRule') return startTurnOff([a.rule], 'single');
    // Turning answering back on needs an active subscription; billing handles that.
    if (!isActivated) return handleNavigate('planSelection');
    const landline = c.numberType === 'landline';
    // Landline setup keeps its own existing flow.
    if (landline) return chooseForwardingNumber();
    switch (a.type) {
      case 'turnOn':
      case 'changeMode':
        return openJourney(reactivationStart(c));
      case 'continueSetup':
        return openJourney(resumeStart(c));
      case 'completeSetup':
        {
          const needs = rulesNeedingSetup(c);
          return openJourney({
            mode: c.mode,
            rules: needs.length ? needs : activeRuleIds(c),
            setup: c.setup,
            screen: 'method'
          });
        }
      case 'reviewSetup':
      case 'checkConnection':
        // Re-run the saved setup steps for the rules that are on.
        return openJourney({
          mode: c.mode,
          rules: activeRuleIds(c),
          setup: c.setup,
          screen: c.setup?.method === 'settings' ? 'settings' : 'code',
          method: c.setup?.method
        });
      case 'setUpRule':
        return openJourney({
          mode: c.mode,
          rules: [a.rule],
          setup: c.setup,
          screen: 'method'
        });
      case 'switchMode':
        if (a.removedAll) {
          // User confirmed all-call forwarding is removed: record it, then let
          // them choose when Vexu answers.
          setConnection(prev => prev ? {
            ...prev,
            rules: {
              ...prev.rules,
              all: 'off'
            }
          } : prev);
          return openJourney({
            mode: 'conditional',
            rules: savedConditionalRules(c),
            setup: c.setup,
            screen: 'mode'
          });
        }
        // The new mode only becomes Active after its forwarding setup is done.
        return openJourney({
          mode: a.mode,
          rules: a.mode === 'all' ? ['all'] : savedConditionalRules(c),
          setup: c.setup,
          screen: 'method'
        });
    }
  };
  // Render current screen content
  const renderScreen = () => {
    switch (currentScreen) {
      // New Onboarding Flow
      case 'welcome':
        return <WelcomeScreen onGetStarted={handleGetStarted} onExplore={handleExplore} data-id="element-2" data-mp="3hedgwgqiq7xl" />;
      case 'features':
        return <FeatureCarouselScreen onContinue={handleFeaturesComplete} data-id="element-3" data-mp="3q6nrhz5bsffd" />;
      case 'phoneEntry':
        return <PhoneEntryScreen onBack={() => setCurrentScreen('features')} onContinue={handlePhoneSubmit} data-id="element-4" data-mp="3v4z2web9qxzp" />;
      case 'otp':
        return <OTPScreen phoneNumber={phoneNumber} initialDeliveryMethod={verificationMethod} onBack={() => setCurrentScreen('phoneEntry')} onVerify={handleOtpVerify} data-id="element-5" data-mp="32gtijso4nb0e" />;
      case 'nameEntry':
        return <NameEntryScreen onBack={() => setCurrentScreen('backupCheck')} onContinue={handleNameComplete} data-id="element-6" data-mp="mq776vnr9ndk" />;
      case 'backupCheck':
        return <BackupCheckScreen onBack={() => setCurrentScreen('otp')} onContinue={() => setCurrentScreen('nameEntry')} data-id="element-7" data-mp="21618pwge6qn" />;
      // Main Tabs
      case 'home':
        return <HomeScreen onNavigate={handleNavigate} connection={connection} subscriptionActive={isActivated} plan={selectedPlan} onForwardingAction={handleForwardingAction} data-id="element-8" data-mp="bz2dxm51h9qv" />;
      case 'bookings':
        return <BookingsScreen onBack={() => handleNavigate('home')} onOpenInCalendar={eventId => {
          setCalendarEventToOpenId(eventId);
          handleNavigate('calendar');
        }} data-id="element-9" data-mp="3ah7umwmwqniw" />;
      case 'calendar':
        return <CalendarScreen onViewCall={callId => {
          setCallToOpenId(callId);
          handleNavigate('calls');
        }} initialEventId={calendarEventToOpenId} onInitialEventOpened={() => setCalendarEventToOpenId(null)} data-id="element-10" data-mp="3kd2m8h93zsxt" />;
      case 'calls':
        return <CallsScreen onNavigate={handleNavigate} onDetailView={setCallDetailOpen} simulatedCall={incomingCall} initialCallId={callToOpenId} onInitialCallOpened={() => setCallToOpenId(null)} onViewBookedEvent={eventId => {
          setCalendarEventToOpenId(eventId);
          handleNavigate('calendar');
        }} forwardingConfigured={Boolean(connection && activeRuleIds(connection).length > 0)} onSetUpCallAnswering={() => handleForwardingAction(connection ? {
          type: 'turnOn'
        } : {
          type: 'setUp'
        })} showDemoTools={showDemoTools} data-id="element-11" data-mp="1785v3ij0c6tb" />;
      case 'usage':
        return <UsageScreen onNavigate={handleNavigate} isActivated={isActivated} isTrial={isTrial} simOverage={simOverage} promoEnabled={promoEnabled} plan={selectedPlan} onPromoChange={setPromoEnabled} connectedNumberType={connection?.numberType ?? null} forwardingActive={connection ? activeRuleIds(connection).length > 0 : undefined} onForwardingDeactivationRequested={numberType => {
          setForwardingNumberType(numberType);
          setCancelAfterForwardingDeactivation(true);
          if (connection && activeRuleIds(connection).length) {
            startTurnOff(activeRuleIds(connection), 'all');
            return;
          }
          setDeactivateIntent('all');
          setDeactivateTarget('all');
          setDeactivateQueue([]);
          handleNavigate('iosDeactivation');
        }} onCancelSubscription={completeSubscriptionCancellation} data-id="element-12" data-mp="1kzu9jsgqb7dg" />;
      case 'profile':
        return <ProfileScreen onNavigate={handleNavigate} isActivated={isActivated} plan={selectedPlan} onLogout={() => {
          setOnboardingComplete(false);
          setIsActivated(false);
          setIsTrial(false);
          setCurrentScreen('welcome');
        }} data-id="element-13" data-mp="3jiqxwbfo0nm5" />;
      // Subscription flow: plan → required consent → card payment → forwarding.
      case 'planSelection':
        return <PlanSelectionScreen onBack={() => handleNavigate(planOrigin)} selectedPlanId={selectedPlanId} promoCode={promoCode} onPromoCodeChange={setPromoCode} onSelectPlan={planId => {
          setSelectedPlanId(planId);
          setHasTrialOffer(true);
          handleNavigate('subscriptionConsent');
        }} data-id="element-14" data-mp="23xomdphozj7g" />;
      case 'subscriptionConsent':
        return <SubscriptionConsentScreen plan={selectedPlan} hasTrialOffer={hasTrialOffer} onBack={() => handleNavigate('planSelection')} onContinue={() => handleNavigate('proCheckout')} data-id="element-15" data-mp="2ro2h5nrnvw5q" />;
      case 'proCheckout':
        return <ProCheckoutScreen plan={selectedPlan} hasTrialOffer={hasTrialOffer} onBack={() => handleNavigate('subscriptionConsent')} onExit={() => handleNavigate('home')} showDemoTools={showDemoTools} promoCode={promoCode} onSuccess={() => {
          setIsActivated(true);
          setIsTrial(hasTrialOffer);
          setForwardingSetupOrigin('checkout');
          handleNavigate('forwardingSetup');
        }} data-id="element-16" data-mp="20n9z7lql4nl0" />;
      case 'forwardingSetup':
        return <ForwardingSetupScreen
        // Payment is done — never send the user back to the card form.
        onBack={() => handleNavigate('home')} paymentConfirmation={forwardingSetupOrigin === 'checkout' ? {
          planName: selectedPlan.name,
          isTrial,
          trialEndsLabel: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric'
          })
        } : undefined} onChooseMobile={() => openJourney(null)} onLandlineActivated={() => {
          setForwardingNumberType('landline');
          // Landline setup isn't verified by a test call yet, so it's
          // saved as reported until a forwarded call reaches Vexu.
          setConnection(prev => ({
            numberType: 'landline',
            phoneNumber: prev?.numberType === 'landline' ? prev.phoneNumber : '+1 (415) 555-0199',
            mode: 'all',
            rules: {
              all: 'reported'
            },
            setup: null,
            draft: null,
            needsAttention: false
          }));
          handleNavigate('proActivationSuccess');
        }} data-id="element-17" data-mp="2sdo44sgmepkj" />;
      case 'proActivationSuccess':
        return <ProActivationSuccessScreen plan={selectedPlan} onComplete={() => handleNavigate('home')} data-id="element-18" data-mp="2zoqmnx7ete50" />;
      case 'iosActivation':
        return <IosActivationFlow journeyStart={journeyStart ?? undefined} subscriptionActive={forwardingNumberType === 'mobile' && !journeyStart} onSubscriptionSuccess={() => {
          setIsActivated(true);
          setIsTrial(true);
          handleNavigate('forwardingSetup');
        }}
        // Only save once real setup has started, so browsing the first
        // screens never turns an inactive account into "incomplete".
        onProgress={p => {
          if (p.draft.screen !== 'mode' && p.draft.screen !== 'method') saveJourney(p, false);
        }} onComplete={(p, destination) => {
          saveJourney(p, true);
          setJourneyStart(null);
          if (!isActivated) {
            setIsActivated(true);
            setIsTrial(true);
          }
          if (destination === 'callerExperience') {
            setCallerExpWelcome(true);
            handleNavigate('callerExperience');
          } else {
            handleNavigate('home');
          }
        }} onCancel={() => {
          setJourneyStart(null);
          handleNavigate('home');
        }} onBack={() => {
          setJourneyStart(null);
          handleNavigate('home');
        }} onContactSupport={() => handleNavigate('support')} data-id="element-19" data-mp="2pque9d5uwxal" />;
      case 'iosDeactivation':
        if ((connection?.numberType ?? forwardingNumberType) === 'landline') {
          return <LandlineDeactivationScreen onBack={() => {
            const backTo = cancelAfterForwardingDeactivation ? 'usage' : 'home';
            setDeactivateTarget(null);
            setCancelAfterForwardingDeactivation(false);
            handleNavigate(backTo);
          }} onComplete={() => {
            if (cancelAfterForwardingDeactivation) {
              completeSubscriptionCancellation();
              return;
            }
            setConnection(prev => prev ? turnAllOff(prev) : prev);
            setVexuStatus(null);
            finishDeactivation();
          }} data-id="element-20" data-mp="32b9eszsdkb72" />;
        }
        {
          const moreQueued = deactivateQueue.length > 0;
          const isLast = !moreQueued && (deactivateIntent === 'all' || !connection || activeRuleIds(connection).filter(r => r !== deactivateTarget).length === 0);
          return <IosDeactivationFlow key={deactivateTarget ?? 'none'} setup={connection?.setup ?? null} conditionId={deactivateTarget as Condition} isLastActiveRule={isLast} onComplete={deactivated => {
            applyRuleOff(deactivated);
            if (moreQueued) {
              // Turning everything off: continue with the next rule.
              setDeactivateTarget(deactivateQueue[0]);
              setDeactivateQueue(deactivateQueue.slice(1));
              return;
            }
            finishDeactivation();
          }} onEnableAnotherRule={() => {
            if (deactivateTarget) applyRuleOff(deactivateTarget);
            finishDeactivation();
          }} onCancelSubscription={completeSubscriptionCancellation} onKeepSubscription={() => {
            if (deactivateTarget) applyRuleOff(deactivateTarget);
            finishDeactivation();
          }} finishAfterDeactivation={cancelAfterForwardingDeactivation && !moreQueued} onBack={() => {
            const backTo = cancelAfterForwardingDeactivation ? 'usage' : 'home';
            setDeactivateTarget(null);
            setDeactivateQueue([]);
            setCancelAfterForwardingDeactivation(false);
            handleNavigate(backTo);
          }} data-id="element-21" data-mp="1fa1odwkaa381" />;
        }
      case 'activationGuide':
        return <ActivationGuideScreen onComplete={() => handleNavigate('home')} data-id="element-22" data-mp="18tf20g3g3iun" />;
      case 'callDetail':
        return <CallDetailScreen onBack={() => handleNavigate('calls')} data-id="element-23" data-mp="1dqowwb9lsmpv" />;
      case 'settings':
        return <SettingsScreen onBack={() => handleNavigate('profile')} data-id="element-24" data-mp="1io71jpgl8fs2" />;
      case 'support':
        return <SupportChatScreen onBack={() => handleNavigate('profile')} data-id="element-25" data-mp="1npwbf1s2qnek" />;
      case 'notifications':
        return <NotificationsScreen onBack={() => handleNavigate('profile')} data-id="element-26" data-mp="1rpwzle4z6cyt" />;
      case 'callerExperience':
        return <CallerExperienceScreen onBack={() => {
          setCallerExpWelcome(false);
          handleNavigate('home');
        }} isActivated={isActivated} onNavigate={handleNavigate} showWelcome={callerExpWelcome} onDismissWelcome={() => setCallerExpWelcome(false)} promoEnabled={promoEnabled} promoBonusMinutes={Math.round(selectedPlan.minutes * 0.2)} onPromoChange={setPromoEnabled} data-id="element-27" data-mp="3ti34mlbecc41" />;
      case 'activeCall':
        // Hidden screen for demo purposes
        return <ActiveCallScreen onEndCall={() => handleNavigate('home')} data-id="element-28" data-mp="8po2z3ozapml" />;
      default:
        return <HomeScreen onNavigate={handleNavigate} data-id="element-29" data-mp="3qull06kmeh5" />;
    }
  };
  // Check if we should show the bottom tab bar
  const showTabBar = onboardingComplete && ['home', 'calendar', 'calls', 'profile'].includes(currentScreen) && !callDetailOpen;
  return <ThemeProvider data-id="element-30" data-mp="2kimau99ldc5x">
      <MobileFrame onSimulateCall={handleSimulateCall} onSimulateEmergencyCall={handleSimulateEmergencyCall} onSimulateFree={handleSimulateFree} onSimulateTrial={handleSimulateTrial} onSimulateConsentWithTrial={() => previewConsentOffer(true)} onSimulateConsentWithoutTrial={() => previewConsentOffer(false)} onSimulateActive={handleSimulateActive} onSimulateOverage={handleSimulateOverage} onToggleVexuNotification={handleToggleVexuNotification} vexuNotificationActive={vexuStatus !== null} vexuNotificationStatus={vexuStatus === 'active' ? 'Active' : vexuStatus === 'busy' ? 'Busy' : vexuStatus === 'dnd' ? 'DND' : 'Off'} data-id="element-31" data-mp="2kj5upvisyaml">
        <div className="h-full flex flex-col relative" data-id="element-32" data-mp="h1pk1b1d1gl0">
          <AnimatePresence data-id="element-33" data-mp="lxmzzg8pokx9">
            {cancellationNotice && currentScreen === 'home' && <CancellationSuccessToast key="cancellation-toast" detail={cancellationNotice} onDismiss={dismissCancellationNotice} data-id="element-34" data-mp="31osnfdc1y2q7" />}
            {vexuStatus && <VexuNotification key="vexu-notification" status={vexuStatus} onTap={() => handleNavigate('home')} data-id="element-35" data-mp="2y9zjjk98z9fu" />}
            {showEmergencyNotification && incomingCall?.isEmergency && <EmergencyCallNotification key="emergency-notification" callerName={incomingCall.name} onTap={openEmergencyCall} data-id="element-36" data-mp="3380ypu3jyom1" />}
            {incomingCall && !incomingCall.isEmergency && ['home', 'calendar', 'profile'].includes(currentScreen) && <IncomingCallBanner key="incoming-banner" call={incomingCall} onTap={() => handleNavigate('calls')} data-id="element-37" data-mp="2kgi1k3w7m4rx" />}
            {showOnboardingWarning && <motion.div key="onboarding-warning" initial={{
            opacity: 0,
            y: -60,
            scale: 0.95
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} exit={{
            opacity: 0,
            y: -40,
            scale: 0.95
          }} transition={{
            type: 'spring',
            stiffness: 300,
            damping: 28
          }} className="absolute top-4 left-3 right-3 z-[60]" data-id="element-38" data-mp="1rqsub8swp8hv">
                <div className="bg-white rounded-2xl p-4 shadow-xl border border-slate-200 flex items-center gap-3" data-id="element-39" data-mp="3hvmmtzi9taud">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0" data-id="element-40" data-mp="3mvs9f19oo75l">
                    <span className="text-lg" data-id="element-41" data-mp="x5f3y3ony6j7">👋</span>
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-42" data-mp="123gnepq047v7">
                    <p className="text-sm font-bold text-slate-900" data-id="element-43" data-mp="1c8f997fy3hk4">
                      Complete setup first
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5" data-id="element-44" data-mp="17an77kll6f27">
                      Login or explore the app, then try simulating a call.
                    </p>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>

          <div className="flex-1 overflow-hidden relative" data-id="element-45" data-mp="tiqilqj4mnak">
            {renderScreen()}
          </div>

          {showTabBar && <BottomTabBar currentTab={activeTab} onTabChange={handleTabChange} data-id="element-46" data-mp="ohj9no58zi2b" />}

          <OnboardingTour isActive={showCallsTour} steps={CALLS_TOUR_STEPS} onComplete={() => setShowCallsTour(false)} onSkip={() => setShowCallsTour(false)} data-id="element-47" data-mp="jluhi5vk53g1" />
        </div>
      </MobileFrame>
    </ThemeProvider>;
}